import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import {
    ChevronLeft, Search, RefreshCw, Copy, Check,
    Smartphone, Tag, Gavel, ArrowLeftRight, MessageSquare,
    Shield, Sparkles, X, CheckCircle2, TrendingUp, Trash2, Zap,
    User, Camera, Save, Loader2, ImagePlus, AtSign, Bot, Store as StoreIcon, Filter as FilterIcon, LayoutGrid
} from 'lucide-react';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { apiFetch } from '@/utils/api';
import { v2Api } from '@/utils/v2Api';
import { useStore } from '@/store/useStore';
import { GramIcon } from '@/components/ui/GramIcon';

interface UsernamesProps {
    onBack: () => void;
    onNavigate?: (page: string, data?: any) => void;
}

interface UsernameItem {
    id: string;
    username: string;
    rawHandle: string;
    phone: string;
    type: string;
    status: string;
    title: string;
    isLocked?: boolean;
    estimatedValue: string;
    listingPrice?: string;
    auctionStartPrice?: string;
    offersEnabled?: boolean;
    isV2?: boolean;
    isBot?: boolean;
    asset_id?: string | number;
    ownership_source?: string | null;
    account?: {
        phone: string;
        name: string;
        avatar: string;
    };
}

interface AccountInfo {
    phone: string;
    phoneDigits: string;
    telegramId: string;
    sessionUsername: string;
    firstName: string;
    lastName: string;
    name: string;
    avatar: string;
    customName?: string | null;
}

export default function Usernames({ onBack }: UsernamesProps) {
    const { t } = useTranslation(['common', 'marketplace', 'profile', 'username']);
    const { impactOccurred, selectionChanged, notificationOccurred } = useHapticFeedback();
    const { user } = useStore();
    const tgRaw: any = typeof window !== 'undefined' ? (window.Telegram?.WebApp?.initDataUnsafe?.user || {}) : {};
    const isAdmin = Boolean(
        user?.role === 'admin' ||
        String(user?.telegram_id) === '601287447' ||
        String(tgRaw.id) === '601287447' ||
        (user as any)?.is_admin
    );
    
    const [usernames, setUsernames] = useState<UsernameItem[]>([]);
    const [v2Listings, setV2Listings] = useState<any[]>([]);
    const [sessions, setSessions] = useState<string[]>([]);
    const [sessionInfo, setSessionInfo] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeFilter, setActiveFilter] = useState('All');
    const [selectedSession, setSelectedSession] = useState<string>('All');
    const [copiedHandle, setCopiedHandle] = useState<string | null>(null);

    // Owner sections (Stock / Channels / Bots) + glass filter toggle
    const [sectionFilter, setSectionFilter] = useState<'all' | 'stock' | 'channels' | 'bots'>('all');
    const [glassFilterOpen, setGlassFilterOpen] = useState(true);

    // Accounts tab state
    const [activeSection, setActiveSection] = useState<'usernames' | 'accounts'>('usernames');
    const [accounts, setAccounts] = useState<AccountInfo[]>([]);
    const [accountsLoading, setAccountsLoading] = useState(false);
    const [editingAccount, setEditingAccount] = useState<AccountInfo | null>(null);
    const [accountName, setAccountName] = useState('');
    const [accountAvatarPreview, setAccountAvatarPreview] = useState<string | null>(null);
    const [accountAvatarFile, setAccountAvatarFile] = useState<File | null>(null);
    const [accountSaving, setAccountSaving] = useState(false);

    // Active Modal state
    const [modalType, setModalType] = useState<'price' | 'auction' | 'offers' | 'transfer' | 'deal' | null>(null);
    const [activeItem, setActiveItem] = useState<UsernameItem | null>(null);

    // Form inputs
    const [fixedPrice, setFixedPrice] = useState('');
    const [auctionPrice, setAuctionPrice] = useState('');
    const [auctionDuration, setAuctionDuration] = useState('24');
    const [minOffer, setMinOffer] = useState('');
    const [offersEnabled, setOffersEnabled] = useState(true);
    const [transferRecipient, setTransferRecipient] = useState('');
    const [toastMessage, setToastMessage] = useState<string | null>(null);

    const showToast = (msg: string) => {
        setToastMessage(msg);
        setTimeout(() => setToastMessage(null), 3000);
    };

    const fetchUsernamesData = async () => {
        try {
            setRefreshing(true);
            let pushItems: UsernameItem[] = [];
            let sessionsList: string[] = [];
            let sessionsInfo: any[] = [];
            // Admins must hit the admin endpoint first, otherwise the user-scoped
            // route wins and they only ever see their own lines (and a partial
            // session list). Non-admins only have the user route.
            const res = isAdmin
                ? await apiFetch('/api/admin/push-usernames')
                : await apiFetch('/api/users/push-usernames');
            if (res.ok) {
                const data = await res.json();
                if (data.success && Array.isArray(data.usernames)) {
                    pushItems = data.usernames;
                    if (Array.isArray(data.sessions)) sessionsList = data.sessions;
                    if (Array.isArray(data.sessionsInfo)) sessionsInfo = data.sessionsInfo;
                }
            } else if (isAdmin) {
                const fallbackRes = await apiFetch('/api/users/push-usernames');
                if (fallbackRes.ok) {
                    const data = await fallbackRes.json();
                    if (data.success && Array.isArray(data.usernames)) {
                        pushItems = data.usernames;
                        if (Array.isArray(data.sessions)) sessionsList = data.sessions;
                    }
                }
            }
            // Keep the admin inventory even when it carries no usernames, otherwise a
            // session with an empty inventory silently disappears from the session list.
            if (sessionsInfo.length) {
                setSessionInfo(sessionsInfo);
            } else if (sessionsList.length) {
                setSessionInfo(sessionsList.map(p => ({ phone: p, digits: String(p).replace(/\D/g, ''), name: null, avatar: null })));
            }

            // ─── New storage (app_market_v2): my registered usernames ───
            const uid = String(user?.telegram_id || '');
            let v2Items: UsernameItem[] = [];
            if (uid) {
                try {
                    const [va, vl] = await Promise.all([
                        v2Api.assets(uid),
                        v2Api.userListings(uid),
                    ]);
                    if (Array.isArray(vl?.listings)) setV2Listings(vl.listings);
                    if (va?.success && va.assets?.usernames?.length) {
                        const mine = va.assets.usernames.filter((u: any) => String(u.current_owner_id ?? u.owner_id ?? '') === uid);
                        const src = mine.length ? mine : va.assets.usernames;
                        v2Items = src.map((u: any) => {
                            const ls = (vl?.listings || []).filter((l: any) => String(l.asset_id) === String(u.asset_id));
                            let status = 'reserved';
                            let listingPrice: string | undefined;
                            if (ls.length) {
                                const t = String(ls[0].listing_type || '').toLowerCase();
                                if (t === 'auction') status = 'auction';
                                else if (t === 'offer') status = 'offers';
                                else if (t === 'store') status = 'listed';
                                const price = Number(ls[0].sale_price ?? ls[0].price);
                                if (price) listingPrice = `${price} GRAM`;
                            }
                            return {
                                id: `v2_${u.asset_id}`,
                                username: u.username,
                                rawHandle: u.username,
                                phone: '',
                                type: /bot$/i.test(u.username) ? 'bot' : 'ownership',
                                isBot: /bot$/i.test(u.username),
                                status,
                                title: 'Registered username',
                                isLocked: false,
                                estimatedValue: '',
                                listingPrice,
                                offersEnabled: true,
                                isV2: true,
                                asset_id: u.asset_id,
                            } as UsernameItem;
                        });
                    }
                } catch (e) {
                    console.error("Failed to load v2 usernames:", e);
                }
            }

            setUsernames([...pushItems, ...v2Items]);
            // Always overwrite, so a stale session list from a previous load can never
            // outlive the data it was built from.
            setSessions(sessionsList);
        } catch (e) {
            console.error("Failed to load Push usernames:", e);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchUsernamesData();
    }, []);

    const fetchAccounts = async () => {
        try {
            setAccountsLoading(true);
            const res = await apiFetch('/api/users/push-accounts');
            if (res.ok) {
                const data = await res.json();
                if (data.success && Array.isArray(data.accounts)) {
                    setAccounts(data.accounts);
                }
            } else {
                const adminRes = await apiFetch('/api/admin/push-accounts');
                if (adminRes.ok) {
                    const data = await adminRes.json();
                    if (data.success && Array.isArray(data.accounts)) {
                        setAccounts(data.accounts);
                    }
                }
            }
        } catch (e) {
            console.error("Failed to load Push accounts:", e);
        } finally {
            setAccountsLoading(false);
        }
    };

    const openAccountEditor = (acc: AccountInfo) => {
        impactOccurred('light');
        setAccountName(acc.name || '');
        setAccountAvatarPreview(null);
        setAccountAvatarFile(null);
        setEditingAccount(acc);
    };

    const handleAvatarPick = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setAccountAvatarFile(file);
        const reader = new FileReader();
        reader.onload = (ev) => setAccountAvatarPreview(ev.target?.result as string | null);
        reader.readAsDataURL(file);
    };

    const saveAccountProfile = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingAccount) return;

        const phone = editingAccount.phoneDigits || editingAccount.phone;
        const acc = editingAccount;
        setAccountSaving(true);

        const formData = new FormData();
        formData.append('phone', phone);
        if (accountName.trim()) formData.append('name', accountName.trim());
        if (accountAvatarFile) formData.append('avatar', accountAvatarFile);

        const optimisticAccount = {
            ...acc,
            name: accountName.trim() || acc.name,
            avatar: accountAvatarPreview || acc.avatar
        };

        setAccounts(prev => prev.map(a => a.phoneDigits === (acc.phoneDigits || a.phoneDigits) ? optimisticAccount : a));
        setEditingAccount(null);
        showToast('Saving account profile...');

        try {
            const res = await apiFetch('/api/users/push-accounts/update', {
                method: 'POST',
                body: formData
            });
            if (!res.ok) {
                await apiFetch('/api/admin/push-accounts/update', {
                    method: 'POST',
                    body: formData
                });
            }
            await fetchAccounts();
            await fetchUsernamesData();
            showToast('Account profile saved');
        } catch (err) {
            console.error("Failed to save account profile:", err);
            showToast('Failed to save account profile');
        } finally {
            setAccountSaving(false);
            setEditingAccount(null);
        }
    };

    const copyToClipboard = (text: string) => {
        impactOccurred('light');
        navigator.clipboard.writeText(text);
        setCopiedHandle(text);
        showToast(`Copied ${text} to clipboard`);
        setTimeout(() => setCopiedHandle(null), 2000);
    };

    const handleActionSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!activeItem || !modalType) return;

        const currentItem = activeItem;
        const currentModal = modalType;

        const resolvedPrice = fixedPrice || auctionPrice || currentItem.listingPrice?.replace(/[^\d.]/g, '') || currentItem.estimatedValue?.replace(/[^\d.]/g, '') || '1000';

        // ─── New storage (app_market_v2) ───
        if (currentItem.isV2) {
            try {
                if (currentModal === 'price') {
                    const d = await v2Api.createUsernameListing({ username: currentItem.username.replace(/^@/, '').trim(), listingType: 'store', price: Number(resolvedPrice) });
                    console.log('[v2 offer] create store:', d);
                    if (!d.success) throw new Error(d.error || 'Failed');
                    setUsernames(prev => prev.map(u => u.id === currentItem.id ? { ...u, status: 'listed', listingPrice: `${resolvedPrice} GRAM` } : u));
                    showToast(`Listed @${currentItem.username} in v2 store`);
                } else if (currentModal === 'auction') {
                    const d = await v2Api.createUsernameListing({ username: currentItem.username.replace(/^@/, '').trim(), listingType: 'auction', price: Number(auctionPrice || resolvedPrice), durationHours: Number(auctionDuration) });
                    console.log('[v2 offer] create auction:', d);
                    if (!d.success) throw new Error(d.error || 'Failed');
                    setUsernames(prev => prev.map(u => u.id === currentItem.id ? { ...u, status: 'auction', auctionStartPrice: `${auctionPrice || resolvedPrice} GRAM` } : u));
                    showToast(`Launched auction for @${currentItem.username} (${auctionDuration}h)`);
                } else if (currentModal === 'offers') {
                    if (offersEnabled) {
                        const d = await v2Api.createUsernameListing({ username: currentItem.username.replace(/^@/, '').trim(), listingType: 'offer', price: Number(minOffer || resolvedPrice) });
                        console.log('[v2 offer] create offer:', d);
                        if (!d.success) throw new Error(d.error || 'Failed');
                        setUsernames(prev => prev.map(u => u.id === currentItem.id ? { ...u, status: 'offers', offersEnabled: true } : u));
                        showToast(`Enabled offers for @${currentItem.username}`);
                    } else {
                        await handleCancelListing({ ...currentItem, status: 'offers' } as UsernameItem, true);
                        showToast(`Disabled offers for @${currentItem.username}`);
                    }
                } else if (currentModal === 'transfer') {
                    showToast('v2 transfer: use the wallet / ownership transfer flow');
                }
            } catch (err: any) {
                console.error('[v2 offer] error:', err.message || err);
                notificationOccurred('error');
                showToast(err.message || 'v2 action failed');
            }
            setModalType(null);
            setActiveItem(null);
            setFixedPrice('');
            setAuctionPrice('');
            setTransferRecipient('');
            await fetchUsernamesData();
            useStore.getState().fetchListings(1, true);
            useStore.getState().fetchAuctions(true);
            return;
        }

        notificationOccurred('success');
        const dealSell = currentModal === 'deal'; // app-owned quick sale → store listing
        const serverAction = dealSell ? 'price' : currentModal;
        if (currentModal === 'price' || currentModal === 'deal') {
            showToast(dealSell ? `Listed ${currentItem.username} for P2P deal at ${resolvedPrice} GRAM` : `Listed ${currentItem.username} for ${resolvedPrice} GRAM`);
        } else if (currentModal === 'auction') {
            setUsernames(prev => prev.map(u => u.id === currentItem.id ? { ...u, status: 'auction', auctionStartPrice: `${resolvedPrice} GRAM` } : u));
            showToast(`Launched auction for ${currentItem.username} at ${resolvedPrice} GRAM (${auctionDuration}h)`);
        } else if (currentModal === 'offers') {
            setUsernames(prev => prev.map(u => u.id === currentItem.id ? { ...u, status: offersEnabled ? 'offers' : 'reserved', offersEnabled } : u));
            showToast(offersEnabled ? `Enabled offers for ${currentItem.username}` : `Disabled offers for ${currentItem.username}`);
        } else if (currentModal === 'transfer') {
            showToast(`Initiated transfer of ${currentItem.username} to ${transferRecipient}`);
        }

        setModalType(null);
        setActiveItem(null);

        const ownershipSource = currentItem.ownership_source === 'app' || dealSell ? 'app' : 'user';
        // App-owned actions are admin-only; user-owned actions go through the normal user route.
        const isAppOwnedAction = ownershipSource === 'app';
        const primaryEndpoint = isAppOwnedAction
            ? '/api/admin/push-usernames/action'
            : '/api/users/push-usernames/action';

        try {
            const res = await apiFetch(primaryEndpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    username: currentItem.username,
                    actionType: serverAction,
                    price: resolvedPrice,
                    duration: auctionDuration,
                    offersEnabled,
                    minOffer,
                    recipient: transferRecipient,
                    ownershipSource
                })
            });
            if (!res.ok && isAppOwnedAction) {
                // App-owned actions have no public fallback — treat as hard failure.
                throw new Error(`App-owned action rejected: ${res.status}`);
            }
            if (!res.ok) {
                await apiFetch('/api/admin/push-usernames/action', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        username: currentItem.username,
                        actionType: serverAction,
                        price: resolvedPrice,
                        duration: auctionDuration,
                        offersEnabled,
                        minOffer,
                        recipient: transferRecipient,
                        ownershipSource
                    })
                });
            }
            await fetchUsernamesData();
            // Force refresh global store listings & auctions cache
            useStore.getState().fetchListings(1, true);
            useStore.getState().fetchAuctions(true);
        } catch (err) {
            console.error("Failed to persist Push action:", err);
        }

        setFixedPrice('');
        setAuctionPrice('');
        setTransferRecipient('');
    };

    const handleCancelListing = async (item: UsernameItem, silent = false) => {
        notificationOccurred('warning');
        setUsernames(prev => prev.map(u => u.id === item.id ? { ...u, status: 'reserved', listingPrice: undefined, auctionStartPrice: undefined } : u));
        if (!silent) showToast(`Unlisted ${item.username} and returned to reserved`);

        // ─── New storage (app_market_v2) ───
        if (item.isV2) {
            try {
                const active = v2Listings.filter((l: any) =>
                    String(l.asset_id) === String(item.asset_id) &&
                    !['cancelled', 'sold', 'deactivated'].includes(String(l.status)) &&
                    String(l.listing_type).toLowerCase() !== 'auction'
                );
                for (const l of active) {
                    try { await v2Api.deactivate(l.id); } catch { /* continue */ }
                }
            } catch (err) {
                console.error('Failed to unlist v2 username:', err);
            }
            await fetchUsernamesData();
            return;
        }

        try {
            await apiFetch('/api/users/push-usernames/action', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    username: item.username,
                    actionType: 'cancel'
                })
            });
            await fetchUsernamesData();
            // Force refresh global store listings & auctions cache
            useStore.getState().fetchListings(1, true);
            useStore.getState().fetchAuctions(true);
        } catch (err) {
            console.error("Failed to cancel Push username listing:", err);
        }
    };

    const filteredUsernames = usernames.filter(item => {
        const matchesSearch = item.username.toLowerCase().includes(searchQuery.toLowerCase()) || 
                              item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                              String(item.phone || '').includes(searchQuery);
        
        // Compare by phone digits — ownership files use '+212...' while some session
        // rows key on bare digits, so an exact string match dropped valid rows.
        const matchesSession = selectedSession === 'All' ||
            String(item.phone || '').replace(/\D/g, '') === String(selectedSession).replace(/\D/g, '');

        const matchesSection =
            sectionFilter === 'all' ||
            (sectionFilter === 'stock' && item.status === 'listed') ||
            (sectionFilter === 'channels' && item.type === 'channel') ||
            (sectionFilter === 'bots' && (item.type === 'bot' || item.isBot));

        const matchesFilter = activeFilter === 'All' ||
            (activeFilter === 'Ownership' && item.type === 'ownership') ||
            (activeFilter === 'Channels' && item.type === 'channel') ||
            (activeFilter === 'Bots' && (item.type === 'bot' || item.isBot)) ||
            (activeFilter === 'Stock' && item.status === 'listed') ||
            (activeFilter === 'Listed' && item.status === 'listed') ||
            (activeFilter === 'Auction' && item.status === 'auction') ||
            (activeFilter === 'Offers' && item.status === 'offers');

        return matchesSearch && matchesSession && matchesSection && matchesFilter;
    });

    const countStock = usernames.filter(i => i.status === 'listed').length;
    const countChannels = usernames.filter(i => i.type === 'channel').length;
    const countBots = usernames.filter(i => i.type === 'bot' || i.isBot).length;

    const totalValuation = usernames.reduce((sum, item) => {
        const num = parseInt(item.estimatedValue) || 0;
        return sum + num;
    }, 0);

    return (
        <div className="min-h-screen bg-[#07090e] text-white pt-3 pb-28 px-4 font-sans select-none relative overflow-x-hidden dir-ltr">
            {/* Top Bar Header */}
            <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                    <button 
                        onClick={() => { impactOccurred('light'); onBack(); }}
                        className="w-10 h-10 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-white active:scale-95 transition-transform"
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h1 className="text-xl font-bold text-white tracking-wide flex items-center gap-2">
                            <span>Push Usernames</span>
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/30 font-mono font-semibold">Push Bot</span>
                        </h1>
                        <p className="text-xs text-white/50">Manage Telegram handles & active sessions</p>
                    </div>
                </div>

                <button 
                    onClick={() => { impactOccurred('medium'); if (activeSection === 'accounts') fetchAccounts(); else fetchUsernamesData(); }}
                    disabled={refreshing || accountsLoading}
                    className="w-10 h-10 rounded-2xl bg-blue-600/20 border border-blue-500/30 flex items-center justify-center text-blue-400 active:scale-95 transition-transform disabled:opacity-50"
                >
                    <RefreshCw className={`w-5 h-5 ${refreshing || accountsLoading ? 'animate-spin' : ''}`} />
                </button>
            </div>

            {/* Section Switcher */}
            <div className="grid grid-cols-2 gap-2 mb-4 bg-white/5 border border-white/10 rounded-2xl p-1.5">
                <button
                    onClick={() => { selectionChanged(); setActiveSection('usernames'); }}
                    className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-all ${
                        activeSection === 'usernames'
                            ? 'bg-gradient-to-r from-blue-600 to-indigo-500 text-white shadow-lg shadow-blue-600/20'
                            : 'text-white/60 hover:text-white'
                    }`}
                >
                    <AtSign className="w-4 h-4" />
                    <span>Usernames</span>
                </button>
                <button
                    onClick={() => { selectionChanged(); setActiveSection('accounts'); if (accounts.length === 0) fetchAccounts(); }}
                    className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-all ${
                        activeSection === 'accounts'
                            ? 'bg-gradient-to-r from-cyan-600 to-teal-500 text-white shadow-lg shadow-cyan-600/20'
                            : 'text-white/60 hover:text-white'
                    }`}
                >
                    <User className="w-4 h-4" />
                    <span>Accounts</span>
                    {accounts.length > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/15 font-mono">{accounts.length}</span>
                    )}
                </button>
            </div>

            {/* Statistics Banner */}
            {activeSection === 'usernames' ? (
            <>
            <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="bg-gradient-to-br from-blue-900/30 to-blue-600/10 border border-blue-500/20 rounded-2xl p-3 flex flex-col justify-between">
                    <div className="flex items-center justify-between text-blue-400 mb-1">
                        <Sparkles className="w-4 h-4" />
                        <span className="text-[10px] text-blue-300/70 font-semibold uppercase">Total Handles</span>
                    </div>
                    <div className="text-xl font-black text-white">{usernames.length}</div>
                    <div className="text-[10px] text-white/40">Active accounts</div>
                </div>

                <div className="bg-gradient-to-br from-purple-900/30 to-purple-600/10 border border-purple-500/20 rounded-2xl p-3 flex flex-col justify-between">
                    <div className="flex items-center justify-between text-purple-400 mb-1">
                        <Smartphone className="w-4 h-4" />
                        <span className="text-[10px] text-purple-300/70 font-semibold uppercase">Sessions</span>
                    </div>
                    <div className="text-xl font-black text-white">{sessions.length}</div>
                    <div className="text-[10px] text-white/40">Connected lines</div>
                </div>

                <div className="bg-gradient-to-br from-emerald-900/30 to-emerald-600/10 border border-emerald-500/20 rounded-2xl p-3 flex flex-col justify-between">
                    <div className="flex items-center justify-between text-emerald-400 mb-1">
                        <TrendingUp className="w-4 h-4" />
                        <span className="text-[10px] text-emerald-300/70 font-semibold uppercase">Est. Value</span>
                    </div>
                    <div className="text-base font-black text-emerald-400 font-mono truncate flex items-center gap-1">
                        <span>{totalValuation.toLocaleString()}</span>
                        <GramIcon className="w-3.5 h-3.5 text-emerald-400" />
                    </div>
                    <div className="text-[10px] text-white/40">GRAM Est.</div>
                </div>
            </div>

            {/* Search Input & Phone Filter */}
            <div className="space-y-3 mb-5">
                <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                        <input
                            type="text"
                            placeholder="Search handle, session phone, or title..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full bg-white/5 border border-white/10 h-11 rounded-2xl pl-10 pr-4 text-sm text-white focus:outline-none focus:border-blue-500/50 placeholder:text-white/30 dir-ltr"
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>

                    {/* Glass filter button */}
                    {isAdmin && (
                    <button
                        onClick={() => { impactOccurred('light'); setGlassFilterOpen(v => !v); }}
                        className={`relative h-11 px-3.5 rounded-2xl flex items-center justify-center gap-1.5 text-xs font-bold transition-all active:scale-95 border ${glassFilterOpen 
                            ? 'bg-gradient-to-br from-cyan-500/30 to-blue-500/20 border-cyan-400/40 text-cyan-300 shadow-lg shadow-cyan-500/20'
                            : 'glass border-white/10 text-white/60 hover:text-white'}`}
                        title="Glass filter"
                    >
                        <FilterIcon className="w-[16px] h-[16px]" />
                        <span>Filter</span>
                        {activeFilter !== 'All' && (
                            <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                        )}
                    </button>
                    )}
                </div>

                {/* Owner sections: Stock / Channels / Bots */}
                {isAdmin && (
                <div className="grid grid-cols-4 gap-1.5">
                    {([
                        { id: 'all', label: 'All', icon: <LayoutGrid size={13} />, count: usernames.length },
                        { id: 'stock', label: 'Stock', icon: <StoreIcon size={13} />, count: countStock },
                        { id: 'channels', label: 'Channels', icon: <Tag size={13} />, count: countChannels },
                        { id: 'bots', label: 'Bots', icon: <Bot size={13} />, count: countBots },
                    ] as any[]).map((s) => (
                        <button
                            key={s.id}
                            onClick={() => { selectionChanged(); setSectionFilter(s.id); }}
                            className={`flex flex-col items-center justify-center gap-1 py-2.5 rounded-2xl text-xs font-bold border transition-all ${
                                sectionFilter === s.id
                                    ? s.id === 'bots'
                                        ? 'bg-cyan-500/25 border-cyan-400/40 text-cyan-200 shadow-lg shadow-cyan-500/10'
                                        : s.id === 'channels'
                                        ? 'bg-emerald-500/25 border-emerald-400/40 text-emerald-200 shadow-lg shadow-emerald-500/10'
                                        : s.id === 'stock'
                                        ? 'bg-amber-500/25 border-amber-400/40 text-amber-200 shadow-lg shadow-amber-500/10'
                                        : 'bg-white/10 border-white/20 text-white'
                                    : 'bg-white/5 border-white/10 text-white/60 hover:text-white'
                            }`}
                        >
                            <span className="flex items-center gap-1">{s.icon}<span>{s.label}</span></span>
                            <span className="text-[9px] font-mono opacity-70">({s.count})</span>
                        </button>
                    ))}
                </div>
                )}

                {/* Session Filter Dropdown / Pills */}
                {isAdmin && sessions.length > 0 && (
                    <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1">
                        <span className="text-xs text-white/40 whitespace-nowrap">Session:</span>
                        <button
                            onClick={() => { selectionChanged(); setSelectedSession('All'); }}
                            className={`px-3 py-1.5 rounded-xl text-xs font-medium whitespace-nowrap border transition-all ${
                                selectedSession === 'All'
                                    ? 'bg-blue-600/30 border-blue-500/50 text-blue-300'
                                    : 'bg-white/5 border-white/10 text-white/60'
                            }`}
                        >
                            All Lines ({usernames.length})
                        </button>
                        {sessions.map((phone, idx) => {
                            const meta = sessionInfo[idx] || {};
                            const digits = meta.digits || String(phone).replace(/\D/g, '');
                            // Count by phone digits, not the raw string: ownership files
                            // store '+212...' while session_profiles keys on digits, so an
                            // exact match reported 0 for lines that actually own handles.
                            const count = usernames.filter(u => String(u.phone || '').replace(/\D/g, '') === digits).length;
                            const label = meta.name || phone;
                            return (
                                <button
                                    key={digits || phone}
                                    onClick={() => { selectionChanged(); setSelectedSession(phone); }}
                                    title={String(phone)}
                                    className={`px-3 py-1.5 rounded-xl text-xs font-medium whitespace-nowrap border transition-all flex items-center gap-1.5 ${
                                        selectedSession === phone
                                            ? 'bg-purple-600/30 border-purple-500/50 text-purple-300'
                                            : 'bg-white/5 border-white/10 text-white/60'
                                    }`}
                                >
                                    {meta.avatar ? (
                                        <img src={meta.avatar} alt="" className="w-3 h-3 rounded-full object-cover shrink-0" />
                                    ) : (
                                        <Smartphone className="w-3 h-3 shrink-0" />
                                    )}
                                    <span>{label}</span>
                                    <span className="text-[10px] opacity-60">({count})</span>
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* Main Filter Chips — always visible; the glass filter button toggles the glass styling */}
                {isAdmin && (
                <div className={`rounded-2xl p-2.5 space-y-1.5 transition-all duration-200 ${glassFilterOpen ? 'glass border border-white/10' : 'border border-transparent'}`}>
                    <p className="text-[9px] uppercase tracking-widest font-bold text-white/40 px-1 flex items-center gap-1">
                        <FilterIcon className="w-3 h-3" /> Status / type filters
                    </p>
                    <div className="flex gap-2 overflow-x-auto no-scrollbar">
                        {[
                            { id: 'All', label: 'All' },
                            { id: 'Ownership', label: 'Personal Handles' },
                            { id: 'Channels', label: 'Channels & Groups' },
                            { id: 'Bots', label: 'Bots' },
                            { id: 'Stock', label: 'Stock' },
                            { id: 'Listed', label: 'Listed for Sale' },
                            { id: 'Auction', label: 'In Auction' },
                            { id: 'Offers', label: 'Open Offers' }
                        ].map((filter) => (
                            <button
                                key={filter.id}
                                onClick={() => { selectionChanged(); setActiveFilter(filter.id); }}
                                className={`px-4 py-2 rounded-xl text-xs font-semibold whitespace-nowrap border transition-all ${
                                    activeFilter === filter.id 
                                        ? 'bg-white text-black border-white shadow-lg shadow-white/10' 
                                        : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10'
                                }`}
                            >
                                {filter.label}
                            </button>
                        ))}
                    </div>
                </div>
                )}
            </div>

            {/* Loading Spinner */}
            {loading ? (
                <div className="flex flex-col items-center justify-center py-16 text-white/40 gap-3">
                    <RefreshCw className="w-8 h-8 animate-spin text-blue-400" />
                    <p className="text-sm">Fetching Push Bot usernames...</p>
                </div>
            ) : filteredUsernames.length === 0 ? (
                <div className="text-center text-white/40 py-16 bg-white/5 border border-white/10 rounded-3xl p-6">
                    <Shield className="w-12 h-12 text-white/20 mx-auto mb-3" />
                    <p className="text-base font-semibold text-white/70">No usernames found</p>
                    <p className="text-xs text-white/40 mt-1">Try adjusting your search query or filters</p>
                </div>
            ) : (
                /* Usernames Cards Grid */
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredUsernames.map((item) => (
                        <div 
                            key={item.id}
                            className="glass border border-white/10 hover:border-blue-500/40 rounded-3xl p-4 transition-all duration-300 shadow-xl backdrop-blur-md relative group flex flex-col justify-between"
                        >
                            {/* Card Top: Handle & Phone */}
                            <div>
                                <div className="flex items-start justify-between gap-2 mb-2">
                                    <div className="flex items-center gap-2">
                                        {item.account?.avatar ? (
                                            <img
                                                src={item.account.avatar}
                                                alt={item.account.name}
                                                className="w-9 h-9 rounded-2xl object-cover border border-blue-500/30"
                                                onError={(e) => { (e.target as HTMLImageElement).src = '/icons/default-avatar.svg'; }}
                                            />
                                        ) : (
                                            <div className="w-9 h-9 rounded-2xl bg-gradient-to-tr from-blue-600 to-indigo-500 flex items-center justify-center text-white font-black text-sm shadow-md">
                                                @
                                            </div>
                                        )}
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h3 className="text-lg font-black text-white dir-ltr font-mono tracking-tight">
                                                    {item.username}
                                                </h3>
                                                <button 
                                                    onClick={() => copyToClipboard(item.username)}
                                                    className="text-white/40 hover:text-white transition-colors p-1"
                                                    title="Copy Handle"
                                                >
                                                    {copiedHandle === item.username ? (
                                                        <Check className="w-4 h-4 text-emerald-400" />
                                                    ) : (
                                                        <Copy className="w-3.5 h-3.5" />
                                                    )}
                                                </button>
                                            </div>
                                            <p className="text-xs text-white/50 line-clamp-1">{item.title}</p>
                                            {item.account?.name && (
                                                <p className="text-[10px] text-cyan-300/80 mt-0.5 font-medium flex items-center gap-1">
                                                    <User className="w-3 h-3" />
                                                    <span className="truncate">{item.account.name}</span>
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Status Badge & Cancel Action */}
                                    <div className="flex items-center gap-1.5">
                                        <span className={`text-[10px] px-2.5 py-1 rounded-full font-bold border uppercase tracking-wider ${
                                            item.status === 'listed'
                                                ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
                                                : item.status === 'auction'
                                                ? 'bg-amber-500/20 text-amber-300 border-amber-500/30 animate-pulse'
                                                : item.status === 'offers'
                                                ? 'bg-violet-500/20 text-violet-300 border-violet-500/30'
                                                : 'bg-blue-500/20 text-blue-300 border-blue-500/30'
                                        }`}>
                                            {item.status === 'listed' ? 'Listed' : item.status === 'auction' ? 'Auction' : item.status === 'offers' ? 'Offers' : 'Push Reserved'}
                                        </span>

                                        {item.ownership_source === 'app' && (
                                            <span className="text-[10px] px-2.5 py-1 rounded-full font-bold border uppercase tracking-wider bg-amber-500/20 text-amber-300 border-amber-500/30">
                                                App
                                            </span>
                                        )}

                                        {(item.type === 'bot' || item.isBot) && (
                                            <span className="text-[10px] px-2.5 py-1 rounded-full font-bold border uppercase tracking-wider bg-cyan-500/20 text-cyan-300 border-cyan-500/30 flex items-center gap-1">
                                                <Bot className="w-3 h-3" /> Bot
                                            </span>
                                        )}

                                        {(item.status === 'listed' || item.status === 'auction' || item.status === 'offers') && (
                                            <button
                                                onClick={() => handleCancelListing(item)}
                                                className="p-1 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/20 active:scale-95 transition-all"
                                                title="Unlist / Cancel Listing"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Meta details bar */}
                                <div className="flex items-center justify-between bg-black/30 border border-white/5 rounded-2xl p-2.5 my-3 text-xs">
                                    <div className="flex items-center gap-1.5 text-white/60 dir-ltr">
                                        <Smartphone className="w-3.5 h-3.5 text-purple-400" />
                                        <span className="font-mono text-[11px]">{item.phone}</span>
                                    </div>
                                    <div className="flex items-center gap-1 text-emerald-400 font-bold font-mono">
                                        <span className="text-[10px] text-white/40">Value:</span>
                                        <span>{item.listingPrice || item.estimatedValue}</span>
                                        <GramIcon className="w-3.5 h-3.5 text-emerald-400" />
                                    </div>
                                </div>
                            </div>

                            {/* Actions Button Bar */}
                            <div className={`grid ${item.ownership_source === 'app' ? (item.status === 'listed' || item.status === 'auction' || item.status === 'offers' ? 'grid-cols-6' : 'grid-cols-5') : (item.status === 'listed' || item.status === 'auction' || item.status === 'offers' ? 'grid-cols-5' : 'grid-cols-4')} gap-1.5 pt-2 border-t border-white/10`}>
                                <button
                                    onClick={() => {
                                        impactOccurred('light');
                                        setActiveItem(item);
                                        setModalType('price');
                                    }}
                                    className="flex flex-col items-center justify-center p-2 rounded-2xl bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 text-emerald-400 active:scale-95 transition-all"
                                    title="List with Fixed Price"
                                >
                                    <Tag className="w-4 h-4 mb-1" />
                                    <span className="text-[10px] font-bold">List Price</span>
                                </button>

                                {item.ownership_source === 'app' && (
                                <button
                                    onClick={() => {
                                        impactOccurred('light');
                                        setActiveItem(item);
                                        setModalType('deal');
                                    }}
                                    className="flex flex-col items-center justify-center p-2 rounded-2xl bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 text-amber-400 active:scale-95 transition-all"
                                    title="Start P2P Deal (app-owned — bot auto-transfers)"
                                >
                                    <Zap className="w-4 h-4 mb-1" />
                                    <span className="text-[10px] font-bold">Sell</span>
                                </button>
                                )}

                                <button
                                    onClick={() => {
                                        impactOccurred('light');
                                        setActiveItem(item);
                                        setModalType('auction');
                                    }}
                                    className="flex flex-col items-center justify-center p-2 rounded-2xl bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 text-amber-400 active:scale-95 transition-all"
                                    title="Start Auction"
                                >
                                    <Gavel className="w-4 h-4 mb-1" />
                                    <span className="text-[10px] font-bold">Auction</span>
                                </button>

                                <button
                                    onClick={() => {
                                        impactOccurred('light');
                                        setActiveItem(item);
                                        setModalType('offers');
                                    }}
                                    className="flex flex-col items-center justify-center p-2 rounded-2xl bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/20 text-purple-400 active:scale-95 transition-all"
                                    title="Offers Settings"
                                >
                                    <MessageSquare className="w-4 h-4 mb-1" />
                                    <span className="text-[10px] font-bold">Offers</span>
                                </button>

                                <button
                                    onClick={() => {
                                        impactOccurred('light');
                                        setActiveItem(item);
                                        setModalType('transfer');
                                    }}
                                    className="flex flex-col items-center justify-center p-2 rounded-2xl bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/20 text-blue-400 active:scale-95 transition-all"
                                    title="Transfer Ownership"
                                >
                                    <ArrowLeftRight className="w-4 h-4 mb-1" />
                                    <span className="text-[10px] font-bold">Transfer</span>
                                </button>

                                {(item.status === 'listed' || item.status === 'auction' || item.status === 'offers') && (
                                    <button
                                        onClick={() => handleCancelListing(item)}
                                        className="flex flex-col items-center justify-center p-2 rounded-2xl bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/20 text-rose-400 active:scale-95 transition-all"
                                        title="Cancel & Unlist"
                                    >
                                        <Trash2 className="w-4 h-4 mb-1" />
                                        <span className="text-[10px] font-bold">Unlist</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            </>
            ) : (
            /* Accounts View */
            <div>
                {accountsLoading ? (
                    <div className="flex flex-col items-center justify-center py-16 text-white/40 gap-3">
                        <RefreshCw className="w-8 h-8 animate-spin text-cyan-400" />
                        <p className="text-sm">Fetching Push accounts...</p>
                    </div>
                ) : accounts.length === 0 ? (
                    <div className="text-center text-white/40 py-16 bg-white/5 border border-white/10 rounded-3xl p-6">
                        <User className="w-12 h-12 text-white/20 mx-auto mb-3" />
                        <p className="text-base font-semibold text-white/70">No accounts found</p>
                        <p className="text-xs text-white/40 mt-1">Active Push sessions will appear here</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {accounts.map((acc) => {
                            const accHandles = usernames.filter(u => u.phone === acc.phone || u.phone === `+${acc.phoneDigits}` || u.phone.replace(/\D/g, '') === acc.phoneDigits);
return (
                                    <div key={acc.phoneDigits || acc.phone} className="glass border border-white/10 hover:border-cyan-500/40 rounded-3xl p-4 transition-all duration-300 shadow-xl backdrop-blur-md relative group">
                                    <div className="flex items-center gap-3">
                                        <div className="relative">
                                            <img
                                                src={accountAvatarPreview && editingAccount?.phoneDigits === acc.phoneDigits ? accountAvatarPreview : (acc.avatar || '/icons/default-avatar.svg')}
                                                alt={acc.name}
                                                className="w-14 h-14 rounded-2xl object-cover border border-cyan-500/30"
                                                onError={(e) => { (e.target as HTMLImageElement).src = '/icons/default-avatar.svg'; }}
                                            />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-base font-black text-white truncate flex items-center gap-2">
                                                <span className="truncate">{acc.name}</span>
                                                {acc.customName && (
                                                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 font-bold uppercase">Custom</span>
                                                )}
                                            </h3>
                                            <p className="text-xs text-white/50 flex items-center gap-1.5 dir-ltr font-mono">
                                                <Smartphone className="w-3 h-3 text-cyan-400" />
                                                <span>{acc.phone}</span>
                                            </p>
                                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                {acc.sessionUsername && (
                                                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/20 font-mono">
                                                        @{acc.sessionUsername}
                                                    </span>
                                                )}
                                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/20 font-mono">
                                                    {accHandles.length} handles
                                                </span>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => openAccountEditor(acc)}
                                            className="p-2.5 rounded-xl bg-cyan-600/20 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-600/30 active:scale-95 transition-all"
                                            title="Edit Name & Avatar"
                                        >
                                            <Camera className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            )}

            {/* Action Modals */}
            {modalType && activeItem && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-end sm:items-center justify-center p-4">
                    <div className="glass-mod rounded-3xl w-full max-w-md p-6 text-left dir-ltr shadow-2xl relative animate-in fade-in zoom-in-95 duration-200">
                        <button 
                            onClick={() => { setModalType(null); setActiveItem(null); }}
                            className="absolute right-4 top-4 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/60 hover:text-white"
                        >
                            <X className="w-4 h-4" />
                        </button>

                        <div className="flex items-center gap-3 mb-4">
                            <img
                                src={activeItem.account?.avatar || '/icons/default-avatar.svg'}
                                alt="Account"
                                className="w-10 h-10 rounded-2xl object-cover border border-blue-500/30"
                                onError={(e) => { (e.target as HTMLImageElement).src = '/icons/default-avatar.svg'; }}
                            />
                            <div>
                                <h2 className="text-lg font-bold text-white font-mono">{activeItem.username}</h2>
                                <p className="text-xs text-white/50">{activeItem.isV2 ? 'Registered · New storage (v2)' : <>Session line: <span className="font-mono">{activeItem.phone}</span></>}</p>
                            </div>
                        </div>

                        <form onSubmit={handleActionSubmit} className="space-y-4">
                            {modalType === 'price' && (
                                <>
                                    <h3 className="text-sm font-bold text-white flex items-center gap-2">
                                        <Tag className="w-4 h-4 text-emerald-400" />
                                        <span>List Handle for Fixed Price</span>
                                    </h3>
                                    <div>
                                        <label className="text-xs text-white/60 flex items-center gap-1 mb-1.5">
                                            <span>Price in GRAM:</span>
                                            <GramIcon className="w-3.5 h-3.5 text-blue-400" />
                                        </label>
                                        <input
                                            type="number"
                                            placeholder="Enter price in GRAM..."
                                            value={fixedPrice}
                                            onChange={(e) => setFixedPrice(e.target.value)}
                                            required
                                            className="w-full bg-white/5 border border-white/10 h-11 rounded-2xl px-4 text-sm text-white focus:outline-none focus:border-emerald-500/50 font-mono"
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        className="w-full h-12 bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-500 hover:to-teal-400 text-white font-bold rounded-2xl shadow-lg shadow-emerald-600/20 active:scale-[0.98] transition-transform"
                                    >
                                        Confirm Fixed Price Listing
                                    </button>
                                </>
                            )}

                            {modalType === 'deal' && (
                                <>
                                    <h3 className="text-sm font-bold text-white flex items-center gap-2">
                                        <Zap className="w-4 h-4 text-amber-400" />
                                        <span>Sell App-Owned Handle</span>
                                    </h3>
                                    <p className="text-xs text-white/60 leading-relaxed">
                                        App-owned username — buyer pays and the bot transfers ownership automatically (no manual confirm).
                                    </p>
                                    <div>
                                        <label className="text-xs text-white/60 flex items-center gap-1 mb-1.5">
                                            <span>Sale Price in GRAM:</span>
                                            <GramIcon className="w-3.5 h-3.5 text-amber-400" />
                                        </label>
                                        <input
                                            type="number"
                                            placeholder="Enter sale price in GRAM..."
                                            value={fixedPrice}
                                            onChange={(e) => setFixedPrice(e.target.value)}
                                            required
                                            className="w-full bg-white/5 border border-white/10 h-11 rounded-2xl px-4 text-sm text-white focus:outline-none focus:border-amber-500/50 font-mono"
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        className="w-full h-12 bg-gradient-to-r from-amber-600 to-orange-500 hover:from-amber-500 hover:to-orange-400 text-white font-bold rounded-2xl shadow-lg shadow-amber-600/20 active:scale-[0.98] transition-transform"
                                    >
                                        Start P2P Sale
                                    </button>
                                </>
                            )}

                            {modalType === 'auction' && (
                                <>
                                    <h3 className="text-sm font-bold text-white flex items-center gap-2">
                                        <Gavel className="w-4 h-4 text-amber-400" />
                                        <span>Launch Handle Auction</span>
                                    </h3>
                                    <div>
                                        <label className="text-xs text-white/60 flex items-center gap-1 mb-1.5">
                                            <span>Starting Price (GRAM)</span>
                                            <GramIcon className="w-3.5 h-3.5 text-amber-400" />
                                        </label>
                                        <input
                                            type="number"
                                            placeholder="Enter starting price..."
                                            value={auctionPrice}
                                            onChange={(e) => setAuctionPrice(e.target.value)}
                                            required
                                            className="w-full bg-white/5 border border-white/10 h-11 rounded-2xl px-4 text-sm text-white focus:outline-none focus:border-amber-500/50 font-mono"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs text-white/60 block mb-1.5">Auction Duration</label>
                                        <select
                                            value={auctionDuration}
                                            onChange={(e) => setAuctionDuration(e.target.value)}
                                            className="w-full bg-[#161b26] border border-white/10 h-11 rounded-2xl px-4 text-sm text-white focus:outline-none"
                                        >
                                            <option value="12">12 Hours</option>
                                            <option value="24">24 Hours (1 Day)</option>
                                            <option value="48">48 Hours (2 Days)</option>
                                            <option value="72">72 Hours (3 Days)</option>
                                        </select>
                                    </div>
                                    <button
                                        type="submit"
                                        className="w-full h-12 bg-gradient-to-r from-amber-600 to-orange-500 hover:from-amber-500 hover:to-orange-400 text-white font-bold rounded-2xl shadow-lg shadow-amber-600/20 active:scale-[0.98] transition-transform"
                                    >
                                        Start Auction Now
                                    </button>
                                </>
                            )}

                            {modalType === 'offers' && (
                                <>
                                    <h3 className="text-sm font-bold text-white flex items-center gap-2">
                                        <MessageSquare className="w-4 h-4 text-purple-400" />
                                        <span>Offers Settings</span>
                                    </h3>
                                    <div className="flex items-center justify-between p-3 bg-white/5 rounded-2xl border border-white/10">
                                        <span className="text-sm text-white/80">Allow Receiving Offers:</span>
                                        <input
                                            type="checkbox"
                                            checked={offersEnabled}
                                            onChange={(e) => setOffersEnabled(e.target.checked)}
                                            className="w-5 h-5 accent-purple-500 rounded"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs text-white/60 block mb-1.5">Min. Offer Amount (Optional):</label>
                                        <input
                                            type="number"
                                            placeholder="Enter minimum offer..."
                                            value={minOffer}
                                            onChange={(e) => setMinOffer(e.target.value)}
                                            className="w-full bg-white/5 border border-white/10 h-11 rounded-2xl px-4 text-sm text-white focus:outline-none focus:border-purple-500/50 font-mono"
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        className="w-full h-12 bg-gradient-to-r from-purple-600 to-indigo-500 hover:from-purple-500 hover:to-indigo-400 text-white font-bold rounded-2xl shadow-lg shadow-purple-600/20 active:scale-[0.98] transition-transform"
                                    >
                                        Save Offers Settings
                                    </button>
                                </>
                            )}

                            {modalType === 'transfer' && (
                                <>
                                    <h3 className="text-sm font-bold text-white flex items-center gap-2">
                                        <ArrowLeftRight className="w-4 h-4 text-blue-400" />
                                        <span>Transfer Ownership</span>
                                    </h3>
                                    <p className="text-xs text-white/60 leading-relaxed">
                                        Transfer handle <span className="font-mono text-blue-400">{activeItem.username}</span> from Push session directly to target recipient.
                                    </p>
                                    <div>
                                        <label className="text-xs text-white/60 block mb-1.5">Recipient Handle or Telegram ID:</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. @username or Telegram ID..."
                                            value={transferRecipient}
                                            onChange={(e) => setTransferRecipient(e.target.value)}
                                            required
                                            className="w-full bg-white/5 border border-white/10 h-11 rounded-2xl px-4 text-sm text-white focus:outline-none focus:border-blue-500/50"
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        className="w-full h-12 bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white font-bold rounded-2xl shadow-lg shadow-blue-600/20 active:scale-[0.98] transition-transform"
                                    >
                                        Initiate Transfer
                                    </button>
                                </>
                            )}
                        </form>
                    </div>
                </div>
            )}

            {/* Account Edit Modal */}
            {editingAccount && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-end sm:items-center justify-center p-4">
                    <div className="glass-mod rounded-3xl w-full max-w-md p-6 text-left dir-ltr shadow-2xl relative animate-in fade-in zoom-in-95 duration-200">
                        <button 
                            onClick={() => { setEditingAccount(null); setAccountAvatarFile(null); setAccountAvatarPreview(null); }}
                            className="absolute right-4 top-4 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/60 hover:text-white"
                        >
                            <X className="w-4 h-4" />
                        </button>

                        <div className="flex items-center gap-3 mb-4">
                            <img
                                src={accountAvatarPreview || editingAccount.avatar || '/icons/default-avatar.svg'}
                                alt="Account"
                                className="w-11 h-11 rounded-2xl object-cover border border-cyan-500/30"
                                onError={(e) => { (e.target as HTMLImageElement).src = '/icons/default-avatar.svg'; }}
                            />
                            <div>
                                <h2 className="text-lg font-bold text-white">Edit Account Profile</h2>
                                <p className="text-xs text-white/50 font-mono">{editingAccount.phone}</p>
                            </div>
                        </div>

                        <form onSubmit={saveAccountProfile} className="space-y-4">
                            <div>
                                <label className="text-xs text-white/60 block mb-1.5">Display Name:</label>
                                <input
                                    type="text"
                                    placeholder="Enter account name..."
                                    value={accountName}
                                    onChange={(e) => setAccountName(e.target.value)}
                                    className="w-full bg-white/5 border border-white/10 h-11 rounded-2xl px-4 text-sm text-white focus:outline-none focus:border-cyan-500/50"
                                />
                            </div>

                            <div>
                                <label className="text-xs text-white/60 block mb-1.5">Avatar (converted to WebP):</label>
                                <label className="flex flex-col items-center justify-center w-full h-28 rounded-2xl border-2 border-dashed border-cyan-500/40 bg-cyan-500/5 hover:bg-cyan-500/10 cursor-pointer transition-all overflow-hidden relative">
                                    {accountAvatarPreview ? (
                                        <img src={accountAvatarPreview} alt="Avatar preview" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="flex flex-col items-center gap-1.5 text-cyan-400">
                                            <ImagePlus className="w-6 h-6" />
                                            <span className="text-xs font-semibold">Tap to choose image</span>
                                            <span className="text-[10px] text-white/40">Stored as lightweight WebP</span>
                                        </div>
                                    )}
                                    <input type="file" accept="image/*" className="hidden" onChange={handleAvatarPick} />
                                </label>
                            </div>

                            <button
                                type="submit"
                                disabled={accountSaving}
                                className="w-full h-12 bg-gradient-to-r from-cyan-600 to-teal-500 hover:from-cyan-500 hover:to-teal-400 text-white font-bold rounded-2xl shadow-lg shadow-cyan-600/20 active:scale-[0.98] transition-transform flex items-center justify-center gap-2 disabled:opacity-60"
                            >
                                {accountSaving ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        <span>Saving...</span>
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4" />
                                        <span>Save Account Profile</span>
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Notification Toast */}
            {toastMessage && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-emerald-600 text-white text-xs font-bold px-4 py-3 rounded-2xl shadow-2xl flex items-center gap-2 border border-emerald-400/30 animate-in slide-in-from-bottom-5">
                    <CheckCircle2 className="w-4 h-4 text-white" />
                    <span>{toastMessage}</span>
                </div>
            )}
        </div>
    );
}
